import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, User2, Mic } from 'lucide-react';
import { useInterview } from '../../contexts/InterviewContext';
import MessageInput from './MessageInput';
import TextMessageContent from './TextMessageContent';
import { Message } from '../../../../types/interviews';

interface ConversationTranscriptProps {
  isMobileView: boolean;
}

const ConversationTranscript: React.FC<ConversationTranscriptProps> = ({ isMobileView }) => {
  const { 
    messages, 
    downloadTranscript,
    userSpeaking,
    isListening,
    isMicEnabled,
    connected,
    sendUserMessage,
    modelTranscript, // Add the model transcription from context
  } = useInterview();
  
  // Add static CSS for the typing animation to the component
  useEffect(() => {
    // Add the animation styles to the document head
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      @keyframes typing {
        from { background-color: rgba(124, 58, 237, 0.2); }
        to { background-color: transparent; }
      }
      .typing-animation {
        position: relative;
      }
      .typing-animation::after {
        content: '|';
        display: inline-block;
        color: #3582F6;
        animation: blink 1.2s step-end infinite;
        margin-left: 2px;
        font-weight: 400;
      }
      @keyframes blink {
        from, to { opacity: 1; }
        50% { opacity: 0; }
      }
    `;
    document.head.appendChild(styleElement);
    
    return () => {
      // Clean up the style element when component unmounts
      document.head.removeChild(styleElement);
    };
  }, []);

  const {userData} = useInterview();
  
  // Ref to access the current transcript text from context
  const { currentTranscriptRef: contextTranscriptRef } = useInterview() as any;
  
  // State to track typing transition
  const [typingTransition, setTypingTransition] = useState<{
    text: string;
    isTransitioning: boolean;
  }>({ text: '', isTransitioning: false });
  
  // Animation state for smooth AI message text updates
  const [animatingMessageId, setAnimatingMessageId] = useState<string | null>(null);
  
  // State to store previous model transcript for animation effects
  const [prevModelTranscript, setPrevModelTranscript] = useState('');
  const [isModelTyping, setIsModelTyping] = useState(false);
  
  // Ref for auto-scrolling
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const lastRenderedMessageIdRef = useRef<string>('');
  const lastTranscriptRef = useRef<string>('');
  
  // Monitor changes to modelTranscript for smooth animations
  useEffect(() => {
    if (modelTranscript !== prevModelTranscript) {
      // If there's new content, trigger the typing animation
      if (modelTranscript && modelTranscript.length > prevModelTranscript.length) {
        setIsModelTyping(true);
        // Cancel typing animation after a short delay
        const timer = setTimeout(() => {
          setIsModelTyping(false);
        }, 500);
        return () => clearTimeout(timer);
      }
      setPrevModelTranscript(modelTranscript);
    }
  }, [modelTranscript, prevModelTranscript]);
  
  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messages.length > 0 && messages[messages.length - 1].id !== lastRenderedMessageIdRef.current) {
      lastRenderedMessageIdRef.current = messages[messages.length - 1].id;
      
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
      
      // If the latest message was from the user, store its text to avoid duplication
      if (messages[messages.length - 1].sender === 'user') {
        lastTranscriptRef.current = messages[messages.length - 1].text;
        
        // If there was a typing indicator, we should transition it out
        if (typingTransition.text) {
          setTypingTransition({
            text: typingTransition.text,
            isTransitioning: true
          });
          
          // Clear the typing transition after animation completes
          setTimeout(() => {
            setTypingTransition({ text: '', isTransitioning: false });
          }, 300); // Match animation duration
        }
      }
      
      // Animate new AI messages with a highlight effect
      if (messages[messages.length - 1].sender === 'ai') {
        setAnimatingMessageId(messages[messages.length - 1].id);
        setTimeout(() => {
          setAnimatingMessageId(null);
        }, 800); // Animation duration
      }
    }
  }, [messages, typingTransition]);
  
  // Track transcript changes
  useEffect(() => {
    if (contextTranscriptRef?.current && userSpeaking) {
      const currentText = contextTranscriptRef.current.trim();
      if (currentText && currentText !== typingTransition.text && !typingTransition.isTransitioning) {
        setTypingTransition({ 
          text: currentText,
          isTransitioning: false
        });
      }
    }
  }, [userSpeaking, contextTranscriptRef?.current, typingTransition]);
  
  // Auto-scroll when the user is speaking
  useEffect(() => {
    if ((userSpeaking || typingTransition.text || modelTranscript) && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [userSpeaking, typingTransition.text, modelTranscript]);

  // Show AI typing indicator when we have modelTranscript but it hasn't been added to messages yet
  const shouldShowAITypingIndicator = () => {
    if (!modelTranscript || !modelTranscript.trim()) {
      return false;
    }
    
    // Check if this transcript is already in the last AI message
    const lastAIMessage = [...messages].reverse().find(m => m.sender === 'ai');
    if (lastAIMessage && lastAIMessage.text === modelTranscript) {
      return false;
    }
    
    return true;
  };

  // Check if the current transcript is the same as the last message
  // This prevents showing the typing indicator when a message was just finalized
  const shouldShowTypingIndicator = () => {
    if (!isListening || !isMicEnabled || 
        typingTransition.isTransitioning ||
        !typingTransition.text) {
      return false;
    }
    
    // Don't show the typing indicator if:
    // 1. It's identical to the last user message
    if (lastTranscriptRef.current && 
        (typingTransition.text === lastTranscriptRef.current || 
         typingTransition.text.includes(lastTranscriptRef.current) || 
         lastTranscriptRef.current.includes(typingTransition.text))) {
      return false;
    }
    
    // Also check all recent user messages (within last 3 seconds)
    const recentUserMessages = messages
      .filter(m => m.sender === 'user' && (Date.now() - new Date(m.timestamp).getTime() < 3000))
      .map(m => m.text);
    
    // Don't show if the text appears in any recent message
    if (recentUserMessages.some(msg => 
        typingTransition.text === msg || 
        typingTransition.text.includes(msg) || 
        msg.includes(typingTransition.text))) {
      return false;
    }
    
    return true;
  };

  // Render message with animation for text updates
  const renderMessage = (message: Message) => {
    const isAnimating = message.id === animatingMessageId || 
                       (message.sender === 'ai' && message.id === messages[messages.length - 1]?.id);
    
    // Check if this is a text message (from function calling)
    const isTextMessage = message.messageType === 'text_message' && message.textMessageData;
                       
    return (
      <div key={message.id} className={`flex items-start ${message.sender === 'ai' ? 'justify-start' : 'justify-end'}`}>
        {/* Add avatar for AI messages */}
        {message.sender === 'ai' && (
          <div className="flex-shrink-0 mr-2">
            <div className="w-6 h-6 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center">
              {/* <User2 size={14} className="text-primary-light/70 dark:text-primary-dark/70" /> */}
              <img src={'/mocktrail-logo-bg-round.png'} height={14} width={14} alt="AI Avatar" className="w-full h-full object-cover" />
            </div>
          </div>
        )}
        
        <div
          className={`${isTextMessage ? 'w-full max-w-2xl' : 'max-w-[85%]'} p-3 rounded-lg ${
            message.sender === 'ai' 
              ? 'bg-primary-light/10 dark:bg-primary-dark/10 border border-primary-light/20 dark:border-primary-dark/20 rounded-tl-none' 
              : 'bg-secondary-light/10 dark:bg-secondary-dark/10 border border-secondary-light/20 dark:border-secondary-dark/20 rounded-tr-none'
          } ${message.id === messages[messages.length - 1].id ? (message.sender === 'ai' ? 'slide-in-left' : 'slide-in-right') : ''}`}
        >
          {isTextMessage ? (
            <TextMessageContent message={message} />
          ) : (
            <>
              <p className={`text-sm text-text-primary-light dark:text-text-primary-dark transition-all duration-500 ease-in-out`}>
                {message.text}
              </p>
              <p className="text-[11px] mt-1 text-text-tertiary-light dark:text-text-tertiary-dark text-right">
                {new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </p>
            </>
          )}
        </div>
        
        {/* Add avatar for user messages */}
        {message.sender === 'user' && (
          <div className="flex-shrink-0 ml-2">
            {userData?.profileImage ? (
              <img 
                src={userData.profileImage} 
                alt={userData.name || 'User'}
                className="w-6 h-6 rounded-full object-cover border border-secondary-light/20 dark:border-secondary-dark/20" 
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-secondary-light/10 dark:bg-secondary-dark/10 border border-secondary-light/20 dark:border-secondary-dark/20 flex items-center justify-center">
                <User2 size={14} className="text-secondary-light/70 dark:text-secondary-dark/70" />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`flex flex-col h-full ${!isMobileView ? 'rounded-xl border border-border-light dark:border-border-dark' : 'rounded-b-xl '} overflow-hidden bg-surface-light/40 dark:bg-surface-dark/40`}>
      {/* Header */}
      <div className="p-3 sm:p-4 bg-background-light/20 dark:bg-background-dark/20 border-b border-border-light dark:border-border-dark flex-shrink-0">
        <h3 className={`${isMobileView ? 'hidden' : ''} font-medium text-base md:text-lg text-text-primary-light dark:text-text-primary-dark font-heading flex items-center`}>
          Messages
          
          {/* Download transcript button */}
          {/* {messages.length > 0 && (
            <button 
              onClick={downloadTranscript}
              className="ml-auto p-1.5 rounded-full hover:bg-background-light/60 dark:hover:bg-background-dark/60 transition-colors"
              title="Download transcript"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-secondary-light dark:text-text-secondary-dark">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </button>
          )} */}
        </h3>
        
        {/* Speech Recognition and Connection Status */}
        <div className={`flex items-center space-x-3 ${isMobileView ? 'mt-0' : 'mt-1'}`}>
          {/* Connection status indicator */}
          <div className="flex items-center">
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'} mr-1`}></div>
            <span className="text-xs text-text-primary-light dark:text-text-primary-dark">
              {connected ? 'AI Ready' : 'AI Not Ready'}
            </span>
          </div>
          
          {/* Speech Recognition Status, Commenting out for now */}
          {/* {isListening && isMicEnabled && !isMicMuted && (
            <div className={`flex items-center ${userSpeaking ? 'text-green-500' : 'text-gray-400'}`}>
              <Mic size={12} className={`mr-1 ${userSpeaking ? 'animate-pulse' : ''}`} />
              <span className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
                {userSpeaking ? 'Speaking...' : 'Ready'}
              </span>
            </div>
          )} */}
        </div>
      </div>

      {/* Messages container - reducing bottom padding from pb-20 to pb-6 */}
      <div className="relative flex-1 overflow-hidden flex flex-col">
        <div 
          ref={scrollContainerRef}
          className="absolute inset-0 overflow-y-auto py-4 px-2 space-y-4 no-scrollbar pb-6"
          style={{ overscrollBehavior: 'contain' }}
        >
          {messages.length > 0 ? (
            <>
              {messages.map(renderMessage)}
              
              {/* Show typing indicator when User is speaking if there's content */}
              {/* {shouldShowTypingIndicator() && (
                <div className={`flex items-start justify-end ${typingTransition.isTransitioning ? 'opacity-0 transition-opacity duration-300' : 'opacity-100'}`}>
                  <div 
                    className="max-w-[85%] p-3 rounded-lg bg-secondary-light/10 dark:bg-secondary-dark/10 border border-secondary-light/20 dark:border-secondary-dark/20 rounded-tr-none slide-in-right"
                  >
                    <p className="text-sm text-text-primary-light dark:text-text-primary-dark">
                      {typingTransition.text}
                      <span className="inline-block w-1.5 h-1.5 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-full animate-bounce ml-1"></span>
                      <span className="inline-block w-1.5 h-1.5 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-full animate-bounce animation-delay-200 ml-1"></span>
                      <span className="inline-block w-1.5 h-1.5 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-full animate-bounce animation-delay-400"></span>
                    </p>
                  </div>
                  
                  <div className="flex-shrink-0 ml-2">
                    {userData?.profileImage ? (
                      <img 
                        src={userData.profileImage} 
                        alt={userData.name || 'User'}
                        className="w-6 h-6 rounded-full object-cover border border-secondary-light/20 dark:border-secondary-dark/20" 
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-secondary-light/10 dark:bg-secondary-dark/10 border border-secondary-light/20 dark:border-secondary-dark/20 flex items-center justify-center">
                        <User2 size={14} className="text-secondary-light/70 dark:text-secondary-dark/70" />
                      </div>
                    )}
                  </div>
                </div>
              )} */}
              
              {/* Updated AI Message Bubble with slower typing animation */}
              {/* {shouldShowAITypingIndicator() && ( */}
                {/* <div className="flex items-start justify-start"> */}
                  {/* AI Avatar */}
                  {/* <div className="flex-shrink-0 mr-2"> */}
                    {/* <div className="w-6 h-6 rounded-full flex items-center justify-center"> */}
                      {/* <img src={'/mocktrail-logo-bg-round.png'} height={14} width={14} alt="AI Avatar" className="w-full h-full object-cover" /> */}
                    {/* </div> */}
                  {/* </div> */}
                  
                  {/* AI Message Bubble with slower typing animation */}
                  {/* <div className="max-w-[85%] p-3 rounded-lg bg-primary-light/10 dark:bg-primary-dark/10 border border-primary-light/20 dark:border-primary-dark/20 rounded-tl-none slide-in-left"> */}
                    {/* <p className={`text-sm text-text-primary-light dark:text-text-primary-dark ${isModelTyping ? 'typing-animation' : ''}`}> */}
                      {/* {modelTranscript} */}
                    {/* </p> */}
                    {/* <p className="text-[11px] mt-1 text-text-tertiary-light dark:text-text-tertiary-dark text-right"> */}
                      {/* {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} */}
                    {/* </p> */}
                  {/* </div> */}
                {/* </div> */}
              {/* )} */}
            </>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center p-6">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-text-tertiary-light dark:text-text-tertiary-dark opacity-50" />
                <h4 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark mb-2">No messages yet</h4>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  You can send a message to the AI interviewer here.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Message Input */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700">
        <MessageInput 
          sendUserMessage={sendUserMessage}
        />
      </div>
    </div>
  );
};

export default ConversationTranscript; 